import React, { useState } from 'react'
import clsx from 'clsx'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useMutation } from '@apollo/client'
import { CHANGE_PASSWORD } from '../../graphql/mutation'
import { toast } from 'react-toastify'

interface Props {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  closeModal: () => void
}

const changePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters long')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])(?=.{8,})/,
      'Must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character'
    )
    .required('Password is required'),

  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters long')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])(?=.{8,})/,
      'Must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character'
    )
    .notOneOf(
      [Yup.ref('oldPassword'), null],
      'New password must be different from the current password'
    )
    .required('Password is required'),

  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], "New Password and Confirm New Password didn't match")
    .required('Password confirmation is required')
})

const ChangePasswordModal: React.FC<Props> = ({ setIsLoading, closeModal }) => {
  const [changePassword, { loading }] = useMutation(CHANGE_PASSWORD, {
    context: {
      headers: {
        'access-control-allow-methods': 'Profile'
      }
    }
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const initialValues = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  }

  const formik = useFormik({
    initialValues,
    validationSchema: changePasswordSchema,
    onSubmit: (values, { resetForm }) => {
      setIsLoading(true)
      changePassword({
        variables: {
          input: {
            oldPassword: values.oldPassword,
            newPassword: values.confirmNewPassword
          }
        }
      })
        .then((res: any) => {
          setIsLoading(false)
          resetForm()
          closeModal()
          if (res.data.changePassword.status >= 200 && res.data.changePassword.status < 300) {
            toast.success(res.data.changePassword.message)
          } else {
            toast.error(res.data.changePassword.message)
          }
        })
        .catch((err) => {
          setIsLoading(false)
          toast.error('Sorry! Unexpected Error.')
          console.error(err)
        })
    }
  })

  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword)
  }

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  return (
    <form
      className='form w-100'
      style={{ maxWidth: '400px' }}
      onSubmit={formik.handleSubmit}
      noValidate
    >
      {/* begin::Form group Current Password */}
      <div className='mb-10 fv-row'>
        <div className='mb-1'>
          <label className='form-label fw-bolder text-dark fs-6'>Current Password</label>
          <div className='position-relative mb-3'>
            <div className='input-group'>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                placeholder='Current Password'
                autoComplete='off'
                {...formik.getFieldProps('oldPassword')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.oldPassword && formik.errors.oldPassword
                  },
                  {
                    'is-valid': formik.touched.oldPassword && !formik.errors.oldPassword
                  }
                )}
              />
              <div className='input-group-append'>
                <span
                  className='form-control form-control-lg form-control-solid'
                  id='togglePassword'
                  onClick={toggleCurrentPasswordVisibility}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '0px 5px 5px 0px' // Adjust the border radius as needed
                  }}
                >
                  {showCurrentPassword ? (
                    <i className='bi bi-eye-slash text-dark'></i>
                  ) : (
                    <i className='bi bi-eye text-dark'></i>
                  )}
                </span>
              </div>
            </div>
            {formik.touched.oldPassword && formik.errors.oldPassword && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.oldPassword}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='text-muted'>
          Use 8 or more characters with a mix of letters, numbers & symbols.
        </div>
      </div>
      {/* end::Form group */}

      {/* begin::Form group New Password */}
      <div className='mb-10 fv-row'>
        <div className='mb-1'>
          <label className='form-label fw-bolder text-dark fs-6'>New Password</label>
          <div className='position-relative mb-3'>
            <div className='input-group'>
              <input
                type={showNewPassword ? 'text' : 'password'}
                placeholder='New Password'
                autoComplete='off'
                {...formik.getFieldProps('newPassword')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.newPassword && formik.errors.newPassword
                  },
                  {
                    'is-valid': formik.touched.newPassword && !formik.errors.newPassword
                  }
                )}
              />
              <div className='input-group-append'>
                <span
                  className='form-control form-control-lg form-control-solid'
                  id='togglePassword'
                  onClick={toggleNewPasswordVisibility}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '0px 5px 5px 0px' // Adjust the border radius as needed
                  }}
                >
                  {showNewPassword ? (
                    <i className='bi bi-eye-slash text-dark'></i>
                  ) : (
                    <i className='bi bi-eye text-dark'></i>
                  )}
                </span>
              </div>
            </div>
            {formik.touched.newPassword && formik.errors.newPassword && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.newPassword}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='text-muted'>
          Use 8 or more characters with a mix of letters, numbers & symbols.
        </div>
      </div>
      {/* end::Form group */}

      {/* begin::Form group Confirm New password */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Confirm New Password</label>
        <div className='input-group'>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder='Confirm New Password'
            autoComplete='off'
            {...formik.getFieldProps('confirmNewPassword')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.confirmNewPassword && formik.errors.confirmNewPassword
              },
              {
                'is-valid': formik.touched.confirmNewPassword && !formik.errors.confirmNewPassword
              }
            )}
          />
          <div className='input-group-append'>
            <span
              className='form-control form-control-lg form-control-solid'
              id='togglePassword'
              onClick={toggleConfirmPasswordVisibility}
              style={{
                cursor: 'pointer',
                borderRadius: '0px 5px 5px 0px' // Adjust the border radius as needed
              }}
            >
              {showConfirmPassword ? (
                <i className='bi bi-eye-slash text-dark'></i>
              ) : (
                <i className='bi bi-eye text-dark'></i>
              )}
            </span>
          </div>
        </div>
        {formik.touched.confirmNewPassword && formik.errors.confirmNewPassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.confirmNewPassword}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      <div className='d-flex justify-content-end'>
        <button
          type='reset'
          className='btn btn-sm btn-white btn-active-light-primary me-2'
          onClick={closeModal}
        >
          Cancel
        </button>

        <button
          type='submit'
          className='btn btn-sm btn-primary'
          disabled={formik.isSubmitting || !formik.isValid || !formik.values.confirmNewPassword}
        >
          {!loading && <span className='indicator-label'>Update</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </form>
  )
}

export default ChangePasswordModal
